// LocalSurfaceFrame — Google Business Profile impression breakdown by
// device x surface (e.g. "Desktop Search", "Mobile Maps"). This is the
// finest cut AA's google-my-business connector exposes: it has NO
// per-physical-location dimension anywhere in its API surface (confirmed
// 2026-08-09), so a per-location split is permanently unfeedable. This
// frame renders the by_surface breakdown instead, sorted descending with a
// total, replacing the dead per-location frame it supersedes.
//
// Self-contained: only depends on recharts and the app's named theme tokens
// (no hardcoded hex for layout, per project convention — same bar pattern
// as ReviewsSentimentFrame.tsx's by_source chart).

import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

export interface LabeledCount {
  label: string;
  value: number;
}

export interface LocalSurfaceFrameProps {
  by_surface: LabeledCount[];
}

// Sample data for standalone preview / storybook-style rendering. Mirrors
// the real Alamo City Urgent Care shape (2026-08-09).
export const demoBySurface: LabeledCount[] = [
  { label: "Mobile Search", value: 30132 },
  { label: "Mobile Maps", value: 15756 },
  { label: "Desktop Search", value: 7233 },
  { label: "Desktop Maps", value: 2578 },
];

const CHART_HEIGHT = 180;
const ROW_HEIGHT = 32;

export function LocalSurfaceFrame({ by_surface }: LocalSurfaceFrameProps) {
  if (!by_surface || by_surface.length === 0) {
    return null;
  }

  const sorted = [...by_surface].sort((a, b) => b.value - a.value);
  const total = sorted.reduce((sum, s) => sum + s.value, 0);
  const height = Math.max(CHART_HEIGHT, sorted.length * ROW_HEIGHT);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <div className="text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
          Impressions by device &amp; surface
        </div>
        <span className="tabular text-dash-context text-muted-foreground">{total.toLocaleString()} total</span>
      </div>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={sorted} layout="vertical" margin={{ top: 4, right: 16, bottom: 0, left: 4 }}>
          <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12, fill: "var(--color-muted-foreground)" }} />
          <YAxis
            type="category"
            dataKey="label"
            width={110}
            tick={{ fontSize: 12, fill: "var(--color-muted-foreground)" }}
          />
          <Tooltip
            contentStyle={{
              borderRadius: 8,
              border: "1px solid var(--color-border)",
              fontSize: 12,
            }}
            formatter={(value: number) => [
              `${value.toLocaleString()} (${total > 0 ? Math.round((value / total) * 100) : 0}%)`,
              "Impressions",
            ]}
          />
          <Bar dataKey="value" fill="var(--color-coral)" radius={[0, 4, 4, 0]} isAnimationActive={false} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
